import axios from 'axios'
import {resolveRecordVesselId, resolveRecordCompanyGroupId, resolveRecordCompanyAdminId} from '../../../../shared/access/tenantScopeMatch'

export type DashboardScope = {companyId?: number; companyType?: string; vesselId?: number}

export function filterDashboardRows<T extends object>(rows: T[], scope: DashboardScope, vessels: object[], crew: object[] = []): T[] {
  if (!scope.companyId && !scope.vesselId) return rows
  const vesselMap = new Map(vessels.map((v: any) => [Number(v.id), v]))
  const crewMap = new Map(crew.map((c: any) => [Number(c.id), c]))
  return rows.filter(row => {
    const record = row as Record<string, any>
    // Historical record vessel takes precedence over the crew's current assignment.
    const owner = resolveRecordVesselId(record) ? record : {...crewMap.get(Number(record.crewId)), ...record}
    if (scope.vesselId && resolveRecordVesselId(owner) !== Number(scope.vesselId)) return false
    if (!scope.companyId) return true
    const company = scope.companyType === 'company_admin' ? resolveRecordCompanyAdminId(owner, vesselMap) : resolveRecordCompanyGroupId(owner, vesselMap)
    return company === Number(scope.companyId)
  })
}

export interface ExpiryRow { crewId: number; crewName: string; document: string; expiry: string }
export const EXPIRY_CACHE_KEY = 'crew-dashboard-expiry-v1'
const CACHE_TTL = 5 * 60 * 1000

export function readExpiryCache(scope: string, now = Date.now()): ExpiryRow[] | null {
  try {
    const cached = JSON.parse(sessionStorage.getItem(EXPIRY_CACHE_KEY) || 'null')
    if (!cached || cached.scope !== scope || !Array.isArray(cached.rows) || now < cached.at || now - cached.at >= CACHE_TTL) return null
    return cached.rows
  } catch { return null }
}

export function writeExpiryCache(scope: string, rows: ExpiryRow[], now = Date.now()): void {
  try { sessionStorage.setItem(EXPIRY_CACHE_KEY, JSON.stringify({scope, rows, at: now})) } catch { /* storage unavailable */ }
}

export function clearExpiryCache(): void {
  try { sessionStorage.removeItem(EXPIRY_CACHE_KEY) } catch { /* storage unavailable */ }
}

const documentFields: Record<string, string> = {
  passportExpiryDate: 'Passport', cdcExpiryDate: 'National CDC', sidExpiryDate: 'Biometric SID',
  contractExpiryDate: 'Contract', medicalExpiryDate: 'Medical', schengenVisaExpiryDate: 'Schengen Visa',
  usVisaC1dExpiryDate: 'US Visa', cocExpiryDate: 'COC', gmdssExpiryDate: 'GMDSS',
  gmdssEndorsementExpiryDate: 'GMDSS Endorsement', indosExpiryDate: 'INDOS', otherDocExpiryDate: 'Other document',
  oilEndorsementExpiryDate: 'Oil endorsement', chemEndorsementExpiryDate: 'Chemical endorsement', gasEndorsementExpiryDate: 'Gas endorsement',
}

export function expiryRows(crew: {id: number; name: string}, documents: any[], courses: any[], watchkeeping: any[], flags: any[]): ExpiryRow[] {
  const rows: ExpiryRow[] = []
  const add = (document: string, expiry: unknown) => {
    if (typeof expiry === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(expiry)) rows.push({crewId: crew.id, crewName: crew.name, document, expiry})
  }
  // Crew Master displays the latest document record when more than one exists.
  const latest = [...documents].sort((a, b) => (b.id || 0) - (a.id || 0))[0]
  if (latest) Object.entries(documentFields).forEach(([key, name]) => add(name, latest[key]))
  courses.forEach(c => add(c.certificateName || 'Course certificate', c.dateOfExpiry))
  watchkeeping.forEach(c => add(c.certificateDetails || 'Watchkeeping certificate', c.validUntil))
  flags.forEach(c => add([c.flag, c.docType].filter(Boolean).join(' - ') || 'Flag document', c.expiryDate))
  return rows
}

export function needsAttention(rows: ExpiryRow[], today = new Date()): ExpiryRow[] {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 30)
  const limit = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`
  return rows.filter(row => row.expiry <= limit).sort((a, b) => a.expiry.localeCompare(b.expiry))
}

export async function loadExpiryRows(crew: {id: number; name: string}[], signal?: AbortSignal, onProgress?: (done: number, total: number) => void): Promise<ExpiryRow[]> {
  const api = process.env.REACT_APP_API_URL
  const result: ExpiryRow[] = []
  let next = 0
  let failed = false
  let completed = 0
  async function worker() {
    while (!failed && next < crew.length) {
      if (signal?.aborted) throw new Error('Document request cancelled')
      const person = crew[next++]
      // The document endpoint enforces READ_CREW_DOCUMENT and crew scope before other sources are queried.
      try {
      const docs = await axios.get(`${api}/crew/${person.id}/documents`, {signal})
      const [courses, watchkeeping, flags] = await Promise.all([
        axios.get(`${api}/crew-course-certificates/crew/${person.id}`, {signal}),
        axios.get(`${api}/crew/${person.id}/watchkeeping-certificates`, {signal}),
        axios.get(`${api}/crew/${person.id}/flag-documents`, {signal}),
      ])
      const arrays = [docs.data, courses.data, watchkeeping.data, flags.data]
      if (!arrays.every(Array.isArray)) throw new Error('Invalid document response')
      result.push(...expiryRows(person, arrays[0], arrays[1], arrays[2], arrays[3]))
      completed++
      onProgress?.(completed, crew.length)
      } catch (error) { failed = true; throw error }
    }
  }
  await Promise.all(Array.from({length: Math.min(4, crew.length)}, () => worker()))
  return needsAttention(result)
}

export async function loadAllotmentRows(crewIds: number[], period: string, signal?: AbortSignal): Promise<{rows: any[]; denied: number}> {
  const ids = Array.from(new Set(crewIds))
  const groups: number[][] = []
  for (let i = 0; i < ids.length; i += 200) groups.push(ids.slice(i, i + 200))
  const pages = await Promise.all(groups.map(async group => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/crew/allotments/overview`, {crewIds: group, period}, {signal})
    if (!Array.isArray(response.data) || response.data.length !== group.length || new Set(response.data.map((r: any) => Number(r.crewId))).size !== group.length || response.data.some((r: any) => !group.includes(Number(r.crewId)) || !Array.isArray(r.allotments))) throw new Error('Allotment access unavailable')
    return {denied: response.data.filter((r: any) => r.allotmentAccessDenied).length, rows: response.data.filter((r: any) => !r.allotmentAccessDenied).flatMap((r: any) => r.allotments.map((a: any) => ({...a, crewId: a.crewId ?? r.crewId})))}
  }))
  return {rows: pages.flatMap(p => p.rows), denied: pages.reduce((sum, p) => sum + p.denied, 0)}
}

export function monthRange(period: string): {start: string; end: string} {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) throw new Error('Choose a valid month')
  const [year, month] = period.split('-').map(Number)
  const lastDay = new Date(year, month, 0).getDate()
  return {start: `${period}-01`, end: `${period}-${lastDay}`}
}

export async function loadRestActivity(records: any[], day: string, signal?: AbortSignal, end = day): Promise<{crewName: string; vesselName: string; entries: number}[]> {
  const unique = new Map<string, any>()
  records.filter(r => r.crewId && r.vesselId && (r.signOnDate ? r.signOnDate.slice(0, 10) <= end && (!r.signOffDate || r.signOffDate.slice(0, 10) >= day) : r.status === 'SIGNED_ON')).forEach(r => unique.set(`${r.crewId}:${r.vesselId}`, r))
  const pairs = Array.from(unique.values())
  const results: {crewName: string; vesselName: string; entries: number}[] = []
  let index = 0
  let failed = false
  async function worker() {
    while (!failed && index < pairs.length) {
      const record = pairs[index++]
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/rest-hours/entries/rest-range`, {params: {crewId: record.crewId, vesselId: record.vesselId, start: day, end}, signal})
        if (!Array.isArray(response.data)) throw new Error('Invalid rest-hour response')
        results.push({crewName: record.crewName || '—', vesselName: record.vesselName || '—', entries: response.data.length})
      } catch (error) { failed = true; throw error }
    }
  }
  await Promise.all(Array.from({length: Math.min(4, pairs.length)}, () => worker()))
  return results
}
